/** @jsx isolateComponent */

import React, { useEffect, useRef, useState } from 'react';
import type { Ref } from 'react';
import type { Interpolation } from '@emotion/css';

import { Link } from '../../navigation/Link';
import { Colors } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

import { NavDropdown } from './NavDropdown';

import type { NavItemType, NavSize, OnChooseItem } from './types';

export type NavRefElement = HTMLDivElement;

export type Props = {
  /**
   * The `aria-current` value to apply to the selected item.
   */
  ariaCurrent?: 'page' | 'step';
  /**
   * Optional label that provides context as to what the navigation is used for.
   * Used by screen reading software.
   */
  ariaLabel?: string;
  /**
   * React ref that is forwarded along as a prop to facilitate
   * typing and to eliminate use of `forwardRef`.
   */
  forwardedRef?: Ref<NavRefElement>;
  /**
   * An array of items to render within the navigation.
   */
  items: Array<NavItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * The `href` of the currently selected navigation item. `href` is
   * used rather than the full item since the item object could be
   * cloned, which ruins `===` checks.
   */
  selectedItemHref: string;
  /**
   * Optional value that will determine if the nav item should be highlighted
   * or not. When not provided, the `href` will be compared to the
   * `selectedItemHref` passed to `Nav`.
   */
  isItemSelected: (item: NavItemType, selectedItemHref: string) => boolean;
  /**
   * Size of navigation. `medium` typically used for small screens
   * and `large` typically used for larger screens.
   */
  size: NavSize;
  /**
   * Whether or not the component should render the grey indicator track beneath the nav items.
   */
  shouldRenderTrack: boolean;
};

const indicatorTrackHeight = 3;

const Styles = labelStyles('Nav', {
  root: (props: Props) => [
    {
      position: 'relative',
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      columnGap: 20,
      rowGap: 2,
      alignItems: 'center',
    },

    props.size === 'medium' &&
      withModifier('medium', {
        height: 50,
        fontSize: 14,
      }),

    props.size === 'large' &&
      withModifier('large', {
        height: 70,
        fontSize: 16,
      }),
  ],

  primary: ({ count }: { count: number }) => [
    {
      display: 'grid',
      height: '100%',
      // Allow shrinking
      minWidth: 0,
      maxWidth: '100%',
    },

    withModifier(`count-${count}`, {
      // `minmax(auto, min-content)` behaves such that at the min value of
      // `auto` items could truncate if necessary, and at the max value of
      // `min-content` items will not render any wider than they need to,
      // ensuring that they are left-aligned rather than stretching to fill
      // any leftover space.
      gridTemplateColumns: `repeat(${count}, minmax(auto, min-content))`,
      gap: 20,
    }),
  ],

  item: ({ isSelected }: { isSelected: boolean }) => [
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 10px',
      textAlign: 'center',
      color: Colors.grey700,
      lineHeight: '20px',
      fontWeight: 600,

      '&:not([disabled]):hover, &:not([disabled]):focus': {
        color: Colors.blue500,
      },

      '&[disabled]': {
        color: Colors.grey500,
        cursor: 'not-allowed',
      },
    },
    isSelected &&
      withModifier('selected', {
        color: Colors.blue500,
      }),
  ],

  itemText: {
    display: 'block',
    whiteSpace: 'nowrap',
  },

  indicatorTrack: (props: Props) => [
    {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      // This ends up being a non-integer, but it actually renders better
      // in Chrome (and still renders fine in other browsers) this way.
      borderRadius: indicatorTrackHeight / 2,
      height: indicatorTrackHeight,
      overflow: 'hidden',
    },
    // The track always renders to the DOM to contain the indicator,
    // but it shouldn't always _visibly_ render.
    props.shouldRenderTrack &&
      withModifier('visible', {
        backgroundColor: Colors.grey100,
      }),
  ],

  indicator: {
    position: 'absolute',
    height: 'inherit',
    borderRadius: 'inherit',
    backgroundColor: Colors.blue500,
    transition: 'all 250ms ease-in-out',
  },
});

type NavItemRefElement = HTMLAnchorElement | HTMLDivElement;

type NavItemProps = NavItemType & {
  ariaCurrent?: 'page' | 'step';
  onChoose: (event: React.MouseEvent) => void;
  forwardedRef: Ref<NavItemRefElement> | null;
  isSelected: boolean;
};

// Since this is a component rather than a DOM node, `forwardRef`
// is used to facilitate properly passing the `ref` along to the DOM node.
const NavItem = (props: NavItemProps) => {
  // Render a `button[disabled]` when `props.disabled` to prevent
  // users from tabbing to it while still providing accessible feedback
  // if users navigate to it in another way. Note that `onClick` and
  // `ref` are also not applied since a disabled `NavItem` shouldn't
  // be able to be clicked (`onClick`) or selected (`ref`).
  if (props.disabled) {
    return (
      <button
        type="button"
        aria-label={props.ariaLabel}
        css={Styles.item(props)}
        disabled={true}
      >
        <span css={Styles.itemText}>{props.text}</span>
      </button>
    );
  }

  return (
    <Link
      ariaLabel={props.ariaLabel}
      ariaCurrent={props.isSelected ? props.ariaCurrent : undefined}
      color={null}
      css={Styles.item(props) as Interpolation}
      href={props.href}
      onClick={props.onChoose}
      ref={props.forwardedRef as Ref<HTMLAnchorElement>}
    >
      <span css={Styles.itemText as Interpolation}>{props.text}</span>
    </Link>
  );
};

/**
 * Creates a horizontal navigation component that can display a variable
 * number of items, and allows excess items to be nested into a dropdown menu.
 */
export const Nav = (props: Props) => {
  // Hold onto ref so the selected node can be measured
  // in order to position `indicator` via `indicatorStyles`.
  const selectedItemRef = useRef<NavItemRefElement>(null);
  const [indicatorStyles, setIndicatorStyles] = useState({});

  const shouldRenderIndicator = !props.items.every((i) => i.isExtra);

  const primaryItems = props.items.filter((item) => !item.isExtra);
  const primaryLinks = primaryItems.map((item) => {
    const isSelected = props.isItemSelected(item, props.selectedItemHref);
    return (
      <NavItem
        {...item}
        ariaCurrent={props.ariaCurrent}
        forwardedRef={
          isSelected && shouldRenderIndicator ? selectedItemRef : null
        }
        isSelected={isSelected}
        key={item.href}
        onChoose={(event) => props.onChooseItem(item, event)}
      />
    );
  });

  const secondaryItems = props.items.filter((item) => item.isExtra);
  const isSecondaryItemSelected = secondaryItems.some((item) => {
    return props.isItemSelected(item, props.selectedItemHref);
  });
  const secondaryMenu = secondaryItems.length > 0 && (
    <div
      ref={
        isSecondaryItemSelected && shouldRenderIndicator
          ? (selectedItemRef as Ref<HTMLDivElement>)
          : null
      }
    >
      <NavDropdown
        items={secondaryItems}
        onChooseItem={props.onChooseItem}
        selectedItemHref={props.selectedItemHref}
        shouldIndicateSelected={shouldRenderIndicator}
        size={props.size}
      />
    </div>
  );

  // Whenever `selectedItemHref` changes, read the position of
  // the `current`ly selected node.
  useEffect(() => {
    // `requestAnimationFrame` ensures rendering has completed
    // and measuring is accurate. Without this some measurements
    // may be off on initial render.
    const requestId = requestAnimationFrame(() => {
      setIndicatorStyles(
        selectedItemRef.current
          ? {
              left: selectedItemRef.current!.offsetLeft,
              width: selectedItemRef.current!.offsetWidth,
            }
          : {}
      );
    });
    return () => cancelAnimationFrame(requestId);
  }, [props.items, props.selectedItemHref]);

  return (
    <nav aria-label={props.ariaLabel} css={Styles.root(props) as Interpolation}>
      <div
        css={Styles.primary({ count: primaryLinks.length }) as Interpolation}
        ref={props.forwardedRef}
      >
        {primaryLinks}
      </div>
      {secondaryMenu}
      <div css={Styles.indicatorTrack(props) as Interpolation}>
        <div css={Styles.indicator as Interpolation} style={indicatorStyles} />
      </div>
    </nav>
  );
};

Nav.defaultProps = {
  ariaCurrent: 'page',
  isItemSelected: (item: NavItemType, selectedItemHref: string) =>
    item.href === selectedItemHref,
  onChooseItem: () => {},
  shouldRenderTrack: true,
  size: 'medium',
};
